import {
  Button,
  Checkbox,
  HStack,
  Link,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@chakra-ui/react';
import { ERROR_CODES } from '@errors/constants';
import { faFilePdf } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { usePDF } from '@react-pdf/renderer';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { isError } from '@sindresorhus/is';
import type { OversizedFile } from '@snap-types/actions/send-snap-attachments';
import { hasMinimumRole } from '@snap-types/features/user';
import { EmailConfigType, UserRole } from '@snap-types/models/enums';
import { Show } from '@ui/components/Show';
import { Router } from '@ui/config/routes';
import { PDFDocument } from '@ui/features/quote-builder/components/PDF/PDFDocument';
import {
  SEND_SNAP_QUOTE_MUTATION_KEY,
  useSendSnapQuoteMutation,
} from '@ui/features/quote-builder/state/endpoints/send-snap-quote';
import { selectIssues } from '@ui/features/quote-builder/state/selectors/issue';
import { selectQuoteBuilder } from '@ui/features/quote-builder/state/selectors/quote-builder';
import { selectQuoteTotal } from '@ui/features/quote-builder/state/selectors/quote-total';
import { mergeFormAndState } from '@ui/features/quote-builder/state/utilities/merge-form-and-state';
import { useCurrentSite } from '@ui/hooks/useCurrentSite';
import { useCurrentSiteAndTeamIds } from '@ui/hooks/useCurrentSiteAndTeamIds';
import { useCurrentUser } from '@ui/hooks/useCurrentUser';
import { useSelector } from '@ui/hooks/useSelector';
import { useValidationToast } from '@ui/hooks/useToast';
import { isApiResponseError } from '@ui/util/api-response-errors';
import { toBase64 } from '@ui/util/base64';
import { normalizeError } from '@ui/util/errors';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { useQuoteBuilderDialog } from '../../hooks/useQuoteBuilderDialog';
import { OversizedFilesDialog } from './OversizedFilesDialog';
import { PDFDisplay } from './PDFDisplay';

/**
 * The Quote PDF Modal depends on a PDF instance from @react-pdf/renderer. To
 * facilitate lazy loading, a portion of the inner modal components are split
 * into their own component (this one). It is expected to be dynamically
 * imported and rendered within a Chakra <Modal />.
 *
 * This component is written with the assumption that it will only be displayed
 * after a quote has been validated. It does not account for cases where the
 * `contactEmail` is missing, or there are other unresolved validation errors in
 * the Quote Builder form.
 */
export function PDFDialogContent() {
  const { currentUser } = useCurrentUser();
  const navigate = useNavigate();
  const currentSite = useCurrentSite();
  if (!currentSite.currentTeam?.currentTeamConfig) {
    throw new Error('Must have a current team config.');
  }
  const { teamCode } = useCurrentSiteAndTeamIds();

  const { quotePdfType } = currentSite.currentTeam.currentTeamConfig;

  const { genericSuccessToast, genericErrorToast } = useValidationToast();
  const sendToCustomerRef = useRef<HTMLInputElement>(null);
  const { close } = useQuoteBuilderDialog({ name: 'pdf' });
  const quoteBuilder = useSelector(selectQuoteBuilder);
  const [sendQuote, { isLoading }] = useSendSnapQuoteMutation({
    fixedCacheKey: SEND_SNAP_QUOTE_MUTATION_KEY,
  });
  const quoteIssues = useSelector((state) =>
    selectIssues(state, { area: 'quote' }),
  );
  const [oversizedFilesDetails, setOversizedFilesDetails] = useState<{
    oversizedFiles: OversizedFile[];
    fileSizeLimitInBytes: number;
  } | null>(null);
  const [showOversizedDialog, setShowOversizedDialog] = useState(false);

  const formattedQuoteTotal = useSelector(selectQuoteTotal);

  const merged = mergeFormAndState(
    quoteBuilder,
    currentUser,
    quoteBuilder.sellingUnitOfMeasure,
    formattedQuoteTotal,
  );

  const [pdf] = usePDF({
    document: <PDFDocument {...merged} quotePdfType={quotePdfType} />,
  });

  const pdfIsValidAndLoaded = pdf.url && pdf.blob && !pdf.loading && !pdf.error;

  const emailSendingEnabledForSite =
    currentSite.currentTeam.currentTeamConfig.quoteEmailSettings !==
    EmailConfigType.DO_NOT_SEND;
  const sendEmailInputDisabled = isLoading || !emailSendingEnabledForSite;

  const handleSendQuote = async (
    skipOversizedImportFiles = false,
    forceIgnoreSizeLimits = false,
  ) => {
    const sendToCustomer = sendToCustomerRef.current?.checked ?? false;

    /**
     * @NOTE(shawk): The send button should be disabled if the PDF is not loaded
     * so this event handler shouldn't be able to be called without a valid PDF
     * but TypeScript is unaware.
     */
    if (!pdf.blob) {
      throw new Error('PDF blob is missing');
    }

    const result = await sendQuote({
      snapQuoteUuid: quoteBuilder.uuid,
      siteUuid: quoteBuilder.siteUuid,
      teamUuid: quoteBuilder.teamUuid,
      sendEmailToCustomer: sendToCustomer,
      quotePdfBase64: await toBase64(pdf.blob),
      skipOversizedImportFiles,
      forceIgnoreSizeLimits,
    });

    if (result.error) {
      handleSendQuoteError(result.error);
    } else {
      await handleSendQuoteSuccess(sendToCustomer);
    }
  };

  const handleSendQuoteError = (
    error: FetchBaseQueryError | SerializedError,
  ) => {
    if (
      isApiResponseError(error) &&
      error.data.code === ERROR_CODES.SNAP_SEND_QUOTE_OVERSIZED_FILE_ERROR
    ) {
      setOversizedFilesDetails({
        oversizedFiles: error.data.oversizedFiles || [],
        fileSizeLimitInBytes: error.data.fileSizeLimitInBytes || 0,
      });

      setShowOversizedDialog(true);
      return;
    }

    if (
      isApiResponseError(error) &&
      error.data.code === ERROR_CODES.SNAP_SEND_QUOTE_DUPLICATE_FILENAME_ERROR
    ) {
      const duplicateNames = (error.data.duplicateFiles ?? []).map(
        (file) => file.fileName,
      );

      genericErrorToast(
        'Duplicate attachment filenames detected',
        duplicateNames.length > 0
          ? `Rename or remove duplicates: ${duplicateNames.join(', ')}`
          : 'Attachment names must be unique before sending to ERP.',
        false,
        true,
      );
      return;
    }

    const e = normalizeError(error, 'Unknown error sending Quote');

    genericErrorToast(
      e?.title ?? `Failed to send quote ${quoteBuilder.quoteId}`,
      e?.description ??
        (isError(error) ? error.message : 'An unknown error occurred'),
      false,
      true,
    );
  };

  const handleSendQuoteSuccess = async (sendToCustomer: boolean) => {
    genericSuccessToast(
      `Quote ${quoteBuilder.quoteId} sent${
        sendToCustomer ? ' to customer' : ''
      }!`,
      undefined,
      false,
      true,
    );

    void navigate(Router.teamPath(teamCode, Router.routes.quoteQueue));
  };

  const handleSendWithoutImports = async () => {
    setShowOversizedDialog(false);
    await handleSendQuote(true, false);
  };

  const handleSendAnyway = async () => {
    setShowOversizedDialog(false);
    await handleSendQuote(false, true);
  };

  const handleCancelOversizedDialog = () => {
    setShowOversizedDialog(false);
    setOversizedFilesDetails(null);
  };

  return (
    <>
      <ModalContent>
        <ModalHeader>Quote {quoteBuilder.quoteId}</ModalHeader>
        <ModalCloseButton isDisabled={isLoading} />
        <ModalBody>
          <PDFDisplay quoteId={quoteBuilder.quoteId} pdf={pdf} />
        </ModalBody>

        <ModalFooter justifyContent="start">
          <HStack width="33%" justifySelf="start">
            <Checkbox
              ref={sendToCustomerRef}
              isDisabled={sendEmailInputDisabled}
              defaultChecked={
                emailSendingEnabledForSite &&
                (currentUser.userSettings?.emailQuoteToCustomer ?? false)
              }
            >
              Send email to customer
            </Checkbox>
          </HStack>

          <HStack width="77%" justifyContent="end">
            <Button
              marginLeft={2}
              gap={2}
              bgColor="#0b7078"
              color="white"
              _hover={{ bg: '#046068', textDecoration: 'none' }}
              _disabled={{ bg: '#aaaaaa' }}
              as={Link}
              href={pdf.url ?? '#'}
              download={`${quoteBuilder.quoteId}-${Date.now()}.pdf`}
              isDisabled={!pdfIsValidAndLoaded || isLoading}
              rounded="sm"
            >
              <FontAwesomeIcon
                icon={faFilePdf}
                fontSize={12}
                aria-label="Download PDF"
              />
              Download PDF
            </Button>
            <Button
              marginLeft={2}
              gap={2}
              bgColor="#0b7078"
              color="white"
              _hover={{ bg: '#046068' }}
              _disabled={{ bg: '#aaaaaa' }}
              onClick={close}
              isDisabled={isLoading}
              rounded="sm"
            >
              Edit Quote
            </Button>
            <Show
              when={hasMinimumRole({
                minimumRole: UserRole.ESTIMATOR,
                user: currentUser,
              })}
            >
              <Button
                marginLeft={2}
                gap={2}
                bgColor="#0b7078"
                color="white"
                _hover={{ bg: '#046068' }}
                _disabled={{ bg: '#aaaaaa' }}
                onClick={() => handleSendQuote()}
                isDisabled={
                  !pdfIsValidAndLoaded ||
                  isLoading ||
                  quoteBuilder.isUpdating ||
                  quoteBuilder.quoteLineUuids.length === 0 ||
                  quoteIssues.length > 0
                }
                isLoading={isLoading || quoteBuilder.isUpdating}
                rounded="sm"
              >
                Send to ERP
              </Button>
            </Show>
          </HStack>
        </ModalFooter>
      </ModalContent>

      <OversizedFilesDialog
        isOpen={showOversizedDialog}
        oversizedFiles={oversizedFilesDetails?.oversizedFiles || []}
        isLoading={isLoading}
        onClose={handleCancelOversizedDialog}
        onSendWithoutImports={handleSendWithoutImports}
        onSendAnyway={handleSendAnyway}
        fileSizeLimitInBytes={oversizedFilesDetails?.fileSizeLimitInBytes || 0}
      />
    </>
  );
}

/** @alias */
export default PDFDialogContent;
